'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import type { Coord } from '@/app/types/restaurant';

export function useLocation() {
  const [userLocation, setUserLocation] = useState<Coord | undefined>(undefined);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [locationError, setLocationError] = useState('');
  const locationInputRef = useRef<HTMLInputElement>(null);

  // Autocomplete location search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (locationSearch.length >= 2) {
        try {
          const encoded = encodeURIComponent(`${locationSearch}, Ho Chi Minh City, Vietnam`);
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=5`,
            { headers: { 'User-Agent': 'FoodFinderSaigon/1.0' } }
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const results = data.map((item: any) => item.display_name.split(',')[0]);
            setLocationSuggestions(results);
            setShowSuggestions(results.length > 0);
          }
        } catch {
          // Silently fail on network errors
        }
      } else {
        setLocationSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [locationSearch]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (locationInputRef.current && !locationInputRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Detect GPS location
  const detectLocation = useCallback(() => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Trinh duyet khong ho tro geolocation');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      (error) => setLocationError('Khong the lay vi tri: ' + error.message)
    );
  }, []);

  // Auto-detect GPS on mount so distance filter works immediately
  useEffect(() => {
    detectLocation();
  }, [detectLocation]);

  // Search location by text query
  const searchLocation = useCallback(async (query?: string) => {
    const q = query || locationSearch;
    if (!q.trim()) return;
    setShowSuggestions(false);
    try {
      const encoded = encodeURIComponent(`${q}, Ho Chi Minh City, Vietnam`);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=1&addressdetails=1`,
        { headers: { 'User-Agent': 'FoodFinderSaigon/1.0' } }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        setUserLocation({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
      } else {
        setLocationError('Không tìm thấy địa điểm');
      }
    } catch {
      setLocationError('Lỗi tìm kiếm địa điểm');
    }
  }, [locationSearch]);

  return {
    userLocation,
    locationSearch,
    setLocationSearch,
    locationSuggestions,
    showSuggestions,
    setShowSuggestions,
    locationError,
    detectLocation,
    searchLocation,
    locationInputRef,
  };
}
